import React, { useRef, useState } from 'react'
import {
  View,
  TouchableOpacity,
  TextInput as RNTextInput,
  StyleSheet,
  StyleProp,
  ViewStyle,
  Image,
} from 'react-native'
import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form'
import { useTheme } from '@react-navigation/native'
import { TextInput } from '../molecules'
import { Icon, IconName } from '../atoms'
import { Text } from '../atoms'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import { defaultImage } from '@assets'
import { Category, usePaginateCategories } from '@hooks'

type FormCategoryListProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  values: Category[]
  isGhost?: boolean
  placeholder?: string
  iconRight?: IconName
  style?: StyleProp<ViewStyle>
  withAutocomplete?: boolean
  addItem: (newValues: Category[]) => void
  removeItem: (newValues: Category[]) => void
}

export const FormCategoryList = <TFieldValues extends FieldValues>({
  control,
  name,
  values = [], // Default to empty array
  isGhost,
  placeholder,  
  iconRight,
  style,
  withAutocomplete,
  addItem,
  removeItem,
  contactId,
}: FormCategoryListProps<TFieldValues> & { contactId?: string }) => {
  const { t } = useLanguage()

  const theme = useTheme()
  const styles = getStyle(theme)

  const inputRef = useRef<RNTextInput>(null)
  const [inputValue, setInputValue] = useState('')
  const { flatData: categories, isFetching: isFetchingCategories, isFetchingNextPage: isFetchingNextPageCategories, fetchNextPage: fetchNextPageCategories, hasNextPage: hasNextPageCategories } = usePaginateCategories(inputValue)
  const [showAutocomplete, setShowAutocomplete] = useState(false)

  const handleAutocompleteSelect = (categ: Category) => {
    addItem([...values, categ])
    setInputValue('')
    setShowAutocomplete(false)
  }

  const handleTextChange = (text: string) => {
    setInputValue(text)
    setShowAutocomplete(text.length > 0)
  }

  const handleRemoveItem = (indexToRemove: number) => {
    const newValue = values.filter((_, index) => index !== indexToRemove)
    removeItem(newValue)
  }

  return (
    <View style={styles.container}>
      <Controller
        control={control}
        name={name}
        render={({ fieldState: { error } }) => (
          <>
            <View style={isGhost ? null : styles.inputContainer}>
              {isGhost && <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.inputTitle}>{name}</Text>}
              <TextInput
                currentRef={inputRef}
                containerStyle={styles.input}
                style={style}
                value={inputValue}
                onChangeText={handleTextChange}
                placeholder={placeholder || t('registerStepHastags.placeholder')}
                returnKeyType="done"
                noBorder={!isGhost}
                iconRight={iconRight}
              />
              {/* Autocomplete dropdown */}
              {withAutocomplete && showAutocomplete && Array.isArray(categories) && categories.length > 0 && (
                <View style={{ backgroundColor: theme.colors.white, borderWidth: 1, borderColor: theme.colors.lightGrey, borderRadius: 8, marginTop: 4, zIndex: 10 }}>
                  {categories.map((c) => (
                    <TouchableOpacity key={c.id} onPress={() => handleAutocompleteSelect(c)} style={{ flexDirection: 'row', alignItems: 'center', padding: 10 }}>
                      <Image source={c.avatarUrl? {uri: c.avatarUrl} : defaultImage} style={styles.avatar}/>
                      <Text type="body">{c.name}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
              <View style={styles.tagsContainer}>
                {values?.map((categ, index) => (
                  <TouchableOpacity
                    key={index}
                    onPress={() => handleRemoveItem(index)}
                    style={styles.tagButton}
                  >
                    <Image source={categ.avatarUrl? {uri: categ.avatarUrl} : defaultImage} style={styles.avatar}/>
                    <Text color={theme.colors.primary} type="bodySmall">
                      {categ.name}
                    </Text>
                    <Icon name="cross" color={theme.colors.primary} size={14} />
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            
            {error && (
              <Text style={styles.errorMessage} type="microText" color={theme.colors.error}>
                {error.message}
              </Text>
            )}
          </>
        )}
      />
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
    },
    inputTitle: {
      marginBottom: theme.spacing.sm,
    },
    inputContainer: {
      flex: 1,
      backgroundColor: theme.colors.background,
      borderRadius: theme.border.large,
      borderWidth: 2,
      borderColor: theme.colors.lightGrey,
      padding: theme.spacing.lg,
      minHeight: 300,
    },
    input: {
      marginBottom: 0,
    },
    tagsContainer: {
      flexWrap: 'wrap',
      flexDirection: 'row',
      marginTop: theme.spacing.md,
    },
    tagButton: {
      flexDirection: 'row',
      alignItems: 'center',
      marginRight: theme.spacing.sm,
      marginBottom: theme.spacing.sm,
      borderWidth: 1,
      borderRadius: theme.border.extraLarge,
      borderColor: theme.colors.primary,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
      backgroundColor: theme.colors.white,
    },
    avatar: {
      width: 25,
      height: 25,
      borderRadius: 13,
      marginRight: theme.spacing.xs
    },
    errorMessage: {
      alignSelf: 'center',
      paddingVertical: theme.spacing.sm,
      color: theme.colors.error,
    },
  })
